<template>
  <div class="dashboard" :style="cssVars">
    <dashboard-layout v-model="configs" :items="configs" :loading="serversLoading" @updateFilter="updateFilter">
      <template #actions v-if="configs.id">
        <b-dropdown-item @click="updateWidgets" aria-role="listitem">{{ $t('dashboards.update') }}</b-dropdown-item>
        <b-dropdown-item v-if="isDashboardEditAllowed" @click="toEditPage" aria-role="listitem">{{
          $t('dashboards.edit')
        }}</b-dropdown-item>
        <b-dropdown-item @click="exportToXls" aria-role="listitem">{{ $t('dashboards.export_xls') }}</b-dropdown-item>
        <b-dropdown-item @click="exportRDA" aria-role="listitem">{{ $t('dashboards.export_rda') }}</b-dropdown-item>
        <!--<b-dropdown-item @click="toDuplicate" aria-role="listitem">{{ $t('dashboards.duplicate') }}</b-dropdown-item>-->
      </template>
    </dashboard-layout>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import DashboardLayout from '@/layout/DashboardLayout'

const { mapState: mapUserState } = createNamespacedHelpers('general')

export default {
  name: 'Dashboard',
  components: {
    DashboardLayout
  },
  computed: {
    ...mapUserState({
      role: state => state.user.role,
      title: state => state.title
    }),
    isAdmin() {
      return this.role === 'ADMIN'
    },
    cssVars() {
      return {
        '--page-background-color': this.configs ? this.configs.background_color : '#ffffff',
        '--page-background-image': this.configs ? `url(${this.configs.background_image})` : 'none'
      }
    }
  },
  data() {
    return {
      isDashboardEditAllowed: false,
      serversLoading: false,
      loadingExport: false,
      updateFilterLoading: false,
      configs: {},
      status: ''
    }
  },
  mounted() {
    if (
      this.$route.query.duplicate !== undefined ||
      this.$route.query.type !== undefined ||
      this.$route.params.dashboard_id !== undefined
    ) {
      this.getDefaultConfig()
    } else {
      this.$router.push({
        name: 'dashboards'
      })
    }
  },
  methods: {
    updateWidgets() {
      const temp = JSON.parse(JSON.stringify(this.configs))
      this.configs = {}
      this.$nextTick(() => {
        this.configs = temp
        this.$forceUpdate()
      })
    },
    toEditPage() {
      this.$router.push({
        name: 'edit_dashboard',
        params: {
          dashboard_id: this.$route.params.dashboard_id
        }
      })
    },
    updateFilter(filters) {
      if (!this.updateFilterLoading) {
        const body = {
          dashboard_id: this.$route.params.dashboard_id,
          element_id: filters.id,
          values: filters.values
        }
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/dashboard/update/filters`
        this.updateFilterLoading = true
        return fetch(url, {
          credentials: 'include',
          body: JSON.stringify(body),
          method: 'POST'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            this.updateWidgets()
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.updateFilterLoading = false
          })
      }
    },
    getDefaultConfig() {
      if (!this.serversLoading) {
        const body = {}
        if (this.$route.params.dashboard_id !== undefined) {
          body.dashboard_id = this.$route.params.dashboard_id
        }
        const searchParams = new URLSearchParams(body)
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/dashboard?${searchParams}`
        this.serversLoading = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            this.configs = data.config
            this.isDashboardEditAllowed = data.is_editable
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.serversLoading = false
          })
      }
    },
    exportToXls() {
      if (!this.loadingExport) {
        const body = {}
        if (this.$route.params.dashboard_id !== undefined) {
          body.dashboard_id = this.$route.params.dashboard_id
        }
        const searchParams = new URLSearchParams(body)
        const url = `${process.env.VUE_APP_API_ENDPOINT ||
          window.location.origin}/api/dashboard/download?${searchParams}`
        this.loadingExport = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            const a = document.createElement('a')
            a.href = data
            a.setAttribute('download', `${this.configs.id}.zip`)
            a.click()
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.loadingExport = false
          })
      }
    },
    exportRDA() {
      if (!this.loadingExport) {
        this.serversLoading = true
        const body = {}
        if (this.$route.params.dashboard_id !== undefined) {
          body.dashboard_id = this.$route.params.dashboard_id
        }
        const searchParams = new URLSearchParams(body)
        const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/api/dashboard/export?${searchParams}`
        this.loadingExport = true
        return fetch(url, {
          method: 'GET',
          credentials: 'include'
        })
          .then(res => {
            if (!res.ok) {
              throw res.json()
            }
            return res.json()
          })
          .then(data => {
            const a = document.createElement('a')
            a.href = data
            a.setAttribute('download', '')
            a.click()
          })
          .catch(error =>
            error.then(data => {
              this.$notify({
                message: JSON.parse(data.message).message,
                type: 'danger'
              })
            })
          )
          .finally(() => {
            this.loadingExport = false
            this.serversLoading = false
          })
      }
    }
  }
}
</script>

<style lang="scss">
.dashboard {
  .dashboard-layout .dash {
    background-color: var(--page-background-color, #fff);
    background-image: var(--page-background-image);
    background-size: 100%;
  }
}
</style>
